"use client";

import { c as _c } from "react/compiler-runtime";
import { AudioLines, ChevronUp, Edit2, ExternalLink, Film, ImageIcon, LogOut, MessageSquare, MoreVerticalIcon, PenTool, Trash2, Users } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTheme } from "next-themes";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, type RowComponentProps } from "react-window";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/ui/logo";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuAction, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useOrganization } from "@/hooks/useOrganization";
import { useDeleteAudioHistory, useRenameAudioHistory } from "@/hooks/usePlaygroundHistory";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { useI18n } from "@llmgateway/i18n/react";
import { getChatUrl, getMarketingUrl } from "@llmgateway/shared/branding";
import { HistorySkeleton } from "./history-skeleton";
import { OrganizationSwitcher } from "./organization-switcher";
import { SidebarChatSearch, SidebarNewAction } from "./sidebar-actions";
import type { AudioGalleryItem } from "@/lib/audio-gen";
import type { Organization } from "@/lib/types";
type Translate = ReturnType<typeof useI18n>["t"];
interface AudioSidebarProps {
  galleryItems: AudioGalleryItem[];
  isHistoryLoading?: boolean;
  onNewChat: () => void;
  onItemClick: (itemId: string) => void;
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization | null) => void;
  currentItemId?: string | null;
  className?: string;
}
type AudioHistoryRow = {
  type: "header";
  key: string;
  title: string;
} | {
  type: "item";
  key: string;
  item: AudioGalleryItem;
} | {
  type: "spacer";
  key: string;
};
const ROW_HEIGHT_HEADER = 28;
const ROW_HEIGHT_SPACER = 6;
const ROW_HEIGHT_ITEM = 60;
interface AudioHistoryRowProps {
  rows: AudioHistoryRow[];
  currentItemId?: string | null;
  editingId: string | null;
  editPrompt: string;
  pendingFocusId: string | null;
  onItemClick: (itemId: string) => void;
  onEditPromptChange: (value: string) => void;
  onSaveEdit: (id: string, original: string) => void;
  onCancelEdit: () => void;
  onDeleteItem: (id: string) => void;
  onStartEdit: (id: string, prompt: string) => void;
  onEditFocused: () => void;
  t: Translate;
}
function getAudioHistoryRowHeight(index: number, {
  rows
}: AudioHistoryRowProps): number {
  const row = rows[index];
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "spacer") {
    return ROW_HEIGHT_SPACER;
  }
  return ROW_HEIGHT_ITEM;
}
function EditAudioPromptInput(t0) {
  const $ = _c(17);
  const {
    itemId,
    value,
    original,
    shouldFocus,
    onChange,
    onSave,
    onCancel,
    onFocused
  } = t0;
  const inputRef = useRef(null);
  const canceledRef = useRef(false);
  let t1;
  let t2;
  if ($[0] !== onFocused || $[1] !== shouldFocus) {
    t1 = () => {
      if (!shouldFocus || !inputRef.current || document.activeElement === inputRef.current) {
        return;
      }
      const el = inputRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
      onFocused();
    };
    t2 = [shouldFocus, onFocused];
    $[0] = onFocused;
    $[1] = shouldFocus;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onChange) {
    t3 = e => onChange(e.target.value);
    $[4] = onChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== itemId || $[7] !== onSave || $[8] !== original) {
    t4 = () => {
      if (!canceledRef.current) {
        onSave(itemId, original);
      }
      canceledRef.current = false;
    };
    $[6] = itemId;
    $[7] = onSave;
    $[8] = original;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== onCancel) {
    t5 = e_0 => {
      if (e_0.key === "Enter") {
        e_0.preventDefault();
        e_0.currentTarget.blur();
      }
      if (e_0.key === "Escape") {
        e_0.preventDefault();
        canceledRef.current = true;
        onCancel();
      }
    };
    $[10] = onCancel;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== value) {
    t6 = <Input ref={inputRef} value={value} onChange={t3} onBlur={t4} onKeyDown={t5} className="h-7 text-sm border-none px-1 focus-visible:ring-0 bg-transparent" />;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = value;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function AudioHistoryRowComponent(t0) {
  const $ = _c(38);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    currentItemId,
    editingId,
    editPrompt,
    pendingFocusId,
    onItemClick,
    onEditPromptChange,
    onSaveEdit,
    onCancelEdit,
    onDeleteItem,
    onStartEdit,
    onEditFocused,
    t
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "spacer") {
    let t1;
    if ($[0] !== style) {
      t1 = <div style={style} aria-hidden="true" />;
      $[0] = style;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (row.type === "header") {
    let t1;
    if ($[2] !== row.title) {
      t1 = <div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{row.title}</div>;
      $[2] = row.title;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== ariaAttributes || $[5] !== style || $[6] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[4] = ariaAttributes;
      $[5] = style;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  const {
    item
  } = row;
  const isEditing = editingId === item.id;
  const isActive = currentItemId === item.id;
  let t1;
  if ($[8] !== item.models) {
    t1 = item.models.every(_temp);
    $[8] = item.models;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const isSaved = t1;
  let t2;
  if ($[10] !== editPrompt || $[11] !== isActive || $[12] !== isEditing || $[13] !== item.id || $[14] !== item.prompt || $[15] !== item.timestamp || $[16] !== onCancelEdit || $[17] !== onEditFocused || $[18] !== onEditPromptChange || $[19] !== onItemClick || $[20] !== onSaveEdit || $[21] !== pendingFocusId) {
    t2 = isEditing ? <div className="flex h-full w-full items-center rounded-md px-2 pr-8 text-left text-sm ring-sidebar-ring bg-sidebar-accent text-sidebar-accent-foreground"><EditAudioPromptInput itemId={item.id} value={editPrompt} original={item.prompt} shouldFocus={pendingFocusId === item.id} onChange={onEditPromptChange} onSave={onSaveEdit} onCancel={onCancelEdit} onFocused={onEditFocused} /></div> : <SidebarMenuButton onClick={() => onItemClick(item.id)} isActive={isActive} className="h-full! w-full justify-start group relative pr-2 !transition-none group-hover/audio-row:pr-9" type="button"><div className="flex items-start gap-2 min-w-0 w-full"><div className="flex h-8 w-8 shrink-0 items-center justify-center rounded border mt-0.5"><AudioLines className="h-4 w-4 text-muted-foreground" /></div><div className="flex-1 min-w-0"><div className="truncate text-sm font-medium mb-0.5">{item.prompt}</div><div className="text-xs text-muted-foreground">{new Date(item.timestamp).toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit"
            })}</div></div></div></SidebarMenuButton>;
    $[10] = editPrompt;
    $[11] = isActive;
    $[12] = isEditing;
    $[13] = item.id;
    $[14] = item.prompt;
    $[15] = item.timestamp;
    $[16] = onCancelEdit;
    $[17] = onEditFocused;
    $[18] = onEditPromptChange;
    $[19] = onItemClick;
    $[20] = onSaveEdit;
    $[21] = pendingFocusId;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  let t3;
  if ($[23] !== isEditing || $[24] !== isSaved || $[25] !== item.id || $[26] !== item.prompt || $[27] !== onDeleteItem || $[28] !== onStartEdit || $[29] !== t) {
    t3 = !isEditing && isSaved && <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5"><DropdownMenu><DropdownMenuTrigger asChild={true}><SidebarMenuAction type="button" onClick={_temp2} className="pointer-events-none static hidden h-7 w-7 cursor-pointer opacity-0 group-hover/audio-row:flex group-hover/audio-row:pointer-events-auto group-hover/audio-row:opacity-100 data-[state=open]:flex data-[state=open]:pointer-events-auto data-[state=open]:opacity-100"><MoreVerticalIcon className="h-3.5 w-3.5" /></SidebarMenuAction></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-40"><DropdownMenuItem onClick={e_0 => {
            e_0.stopPropagation();
            onStartEdit(item.id, item.prompt);
          }} className="flex items-center gap-2"><Edit2 className="h-4 w-4" />{t("playground.rename")}</DropdownMenuItem><DropdownMenuItem onClick={e_1 => {
            e_1.stopPropagation();
            onDeleteItem(item.id);
          }} className="flex items-center gap-2 text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />{t("common.delete")}</DropdownMenuItem></DropdownMenuContent></DropdownMenu></div>;
    $[23] = isEditing;
    $[24] = isSaved;
    $[25] = item.id;
    $[26] = item.prompt;
    $[27] = onDeleteItem;
    $[28] = onStartEdit;
    $[29] = t;
    $[30] = t3;
  } else {
    t3 = $[30];
  }
  let t4;
  if ($[31] !== t2 || $[32] !== t3) {
    t4 = <div className="relative h-full px-2 pb-1"><div className="group/audio-row relative h-full">{t2}{t3}</div></div>;
    $[31] = t2;
    $[32] = t3;
    $[33] = t4;
  } else {
    t4 = $[33];
  }
  let t5;
  if ($[34] !== ariaAttributes || $[35] !== style || $[36] !== t4) {
    t5 = <div {...ariaAttributes} style={style}>{t4}</div>;
    $[34] = ariaAttributes;
    $[35] = style;
    $[36] = t4;
    $[37] = t5;
  } else {
    t5 = $[37];
  }
  return t5;
}
function _temp2(e) {
  e.stopPropagation();
}
function _temp(m) {
  return !m.isLoading;
}
function groupItemsByDate(items: AudioGalleryItem[]) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as AudioGalleryItem[],
    yesterday: [] as AudioGalleryItem[],
    lastWeek: [] as AudioGalleryItem[],
    older: [] as AudioGalleryItem[]
  };
  items.forEach(item => {
    const itemDate = new Date(item.timestamp);
    if (itemDate.toDateString() === today.toDateString()) {
      groups.today.push(item);
    } else if (itemDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(item);
    } else if (itemDate >= lastWeek) {
      groups.lastWeek.push(item);
    } else {
      groups.older.push(item);
    }
  });
  return groups;
}
export function AudioSidebar(t0) {
  const $ = _c(290);
  const {
    galleryItems,
    isHistoryLoading: t1,
    onNewChat,
    onItemClick,
    organizations,
    selectedOrganization,
    onSelectOrganization,
    currentItemId,
    className
  } = t0;
  const isHistoryLoading = t1 === undefined ? false : t1;
  const {
    t
  } = useI18n();
  let switcherOrganizations;
  let t2;
  if ($[0] !== organizations || $[1] !== selectedOrganization?.id) {
    switcherOrganizations = organizations.filter(_temp3);
    t2 = switcherOrganizations.find(org_0 => org_0.id === selectedOrganization?.id) ?? null;
    $[0] = organizations;
    $[1] = selectedOrganization?.id;
    $[2] = switcherOrganizations;
    $[3] = t2;
  } else {
    switcherOrganizations = $[2];
    t2 = $[3];
  }
  const switcherSelectedOrganization = t2;
  const listContainerRef = useRef(null);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t3;
  if ($[4] !== searchParams) {
    t3 = searchParams.get("orgId");
    $[4] = searchParams;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const orgIdParam = t3;
  let t4;
  if ($[6] !== orgIdParam) {
    t4 = path => orgIdParam ? `${path}?orgId=${orgIdParam}` : path;
    $[6] = orgIdParam;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const withOrg = t4;
  const posthog = usePostHog();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    organization,
    isLoading: isOrgLoading
  } = useOrganization();
  let t5;
  if ($[8] !== posthog || $[9] !== router || $[10] !== signOut) {
    t5 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : getChatUrl("/login"));
          }
        }
      });
    };
    $[8] = posthog;
    $[9] = router;
    $[10] = signOut;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const logout = t5;
  const {
    theme,
    setTheme,
    systemTheme
  } = useTheme();
  const currentTheme = theme === "system" ? systemTheme : theme;
  let t6;
  if ($[12] !== currentTheme || $[13] !== setTheme) {
    t6 = () => {
      setTheme(currentTheme === "dark" ? "light" : "dark");
    };
    $[12] = currentTheme;
    $[13] = setTheme;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const toggleTheme = t6;
  const renameItem = useRenameAudioHistory();
  const deleteItem = useDeleteAudioHistory();
  const [editingId, setEditingId] = useState(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [pendingFocusId, setPendingFocusId] = useState(null);
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (id, prompt) => {
      setEditingId(id);
      setEditPrompt(prompt);
      setPendingFocusId(id);
    };
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const startEdit = t7;
  let t8;
  if ($[16] !== editPrompt || $[17] !== renameItem) {
    t8 = (id_0, original) => {
      const next = editPrompt.trim();
      if (next && next !== original) {
        renameItem.mutate({
          params: {
            path: {
              id: id_0
            }
          },
          body: {
            prompt: next
          }
        });
      }
      setEditingId(null);
      setEditPrompt("");
      setPendingFocusId(null);
    };
    $[16] = editPrompt;
    $[17] = renameItem;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const saveEdit = t8;
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setEditingId(null);
      setEditPrompt("");
      setPendingFocusId(null);
    };
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const cancelEdit = t9;
  let t10;
  if ($[20] !== deleteItem) {
    t10 = id_1 => {
      deleteItem.mutate({
        params: {
          path: {
            id: id_1
          }
        }
      });
    };
    $[20] = deleteItem;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const handleDeleteItem = t10;
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setPendingFocusId(null);
    };
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const onEditFocused = t11;
  let t12;
  if ($[23] !== galleryItems) {
    t12 = [...galleryItems].sort(_temp4);
    $[23] = galleryItems;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const sortedItems = t12;
  let t13;
  if ($[25] !== sortedItems) {
    t13 = groupItemsByDate(sortedItems);
    $[25] = sortedItems;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const itemGroups = t13;
  let rows;
  if ($[27] !== itemGroups.lastWeek || $[28] !== itemGroups.older || $[29] !== itemGroups.today || $[30] !== itemGroups.yesterday || $[31] !== t) {
    const groups = [{
      title: t("playground.today"),
      items: itemGroups.today
    }, {
      title: t("playground.yesterday"),
      items: itemGroups.yesterday
    }, {
      title: t("playground.last7Days"),
      items: itemGroups.lastWeek
    }, {
      title: t("playground.older"),
      items: itemGroups.older
    }];
    rows = [];
    groups.forEach((t14, groupIndex) => {
      const {
        title,
        items: groupedItems
      } = t14;
      if (groupedItems.length === 0) {
        return;
      }
      rows.push({
        type: "header",
        key: `header-${title}`,
        title
      });
      groupedItems.forEach(item => {
        rows.push({
          type: "item",
          key: `item-${item.id}`,
          item
        });
      });
      const hasNextGroupWithItems = groups.slice(groupIndex + 1).some(_temp5);
      if (hasNextGroupWithItems) {
        rows.push({
          type: "spacer",
          key: `spacer-${title}`
        });
      }
    });
    $[27] = itemGroups.lastWeek;
    $[28] = itemGroups.older;
    $[29] = itemGroups.today;
    $[30] = itemGroups.yesterday;
    $[31] = t;
    $[32] = rows;
  } else {
    rows = $[32];
  }
  const historyRows = rows;
  let t14;
  if ($[33] !== currentItemId || $[34] !== editPrompt || $[35] !== editingId || $[36] !== handleDeleteItem || $[37] !== historyRows || $[38] !== onItemClick || $[39] !== pendingFocusId || $[40] !== saveEdit || $[41] !== t) {
    t14 = {
      rows: historyRows,
      currentItemId,
      editingId,
      editPrompt,
      pendingFocusId,
      onItemClick,
      onEditPromptChange: setEditPrompt,
      onSaveEdit: saveEdit,
      onCancelEdit: cancelEdit,
      onDeleteItem: handleDeleteItem,
      onStartEdit: startEdit,
      onEditFocused,
      t
    };
    $[33] = currentItemId;
    $[34] = editPrompt;
    $[35] = editingId;
    $[36] = handleDeleteItem;
    $[37] = historyRows;
    $[38] = onItemClick;
    $[39] = pendingFocusId;
    $[40] = saveEdit;
    $[41] = t;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const rowProps = t14;
  const isAuthenticated = !!user;
  const isHistoryHidden = sidebarState === "collapsed" && !isMobile;
  if (isUserLoading) {
    let t15;
    let t16;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Logo className="size-6" />;
      t16 = <h1 className="text-xl font-semibold">doteb</h1>;
      $[43] = t15;
      $[44] = t16;
    } else {
      t15 = $[43];
      t16 = $[44];
    }
    let t17;
    if ($[45] !== t) {
      t17 = t("studio.audio");
      $[45] = t;
      $[46] = t17;
    } else {
      t17 = $[46];
    }
    let t18;
    if ($[47] !== t17) {
      t18 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4"><Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}>{t15}{t16}<Badge>{t17}</Badge></Link></div></SidebarHeader>;
      $[47] = t17;
      $[48] = t18;
    } else {
      t18 = $[48];
    }
    let t19;
    if ($[49] !== className || $[50] !== t18) {
      t19 = <Sidebar className={className}>{t18}</Sidebar>;
      $[49] = className;
      $[50] = t18;
      $[51] = t19;
    } else {
      t19 = $[51];
    }
    return t19;
  }
  if (!isAuthenticated) {
    let t15;
    let t16;
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Logo className="size-6" />;
      t16 = <h1 className="text-xl font-semibold">doteb</h1>;
      $[52] = t15;
      $[53] = t16;
    } else {
      t15 = $[52];
      t16 = $[53];
    }
    let t17;
    if ($[54] !== t) {
      t17 = t("studio.audio");
      $[54] = t;
      $[55] = t17;
    } else {
      t17 = $[55];
    }
    let t18;
    if ($[56] !== t17) {
      t18 = <Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}>{t15}{t16}<Badge>{t17}</Badge></Link>;
      $[56] = t17;
      $[57] = t18;
    } else {
      t18 = $[57];
    }
    let t19;
    if ($[58] !== t) {
      t19 = t("playground.signInRequired");
      $[58] = t;
      $[59] = t19;
    } else {
      t19 = $[59];
    }
    let t20;
    if ($[60] !== t19) {
      t20 = <div className="font-medium mb-2">{t19}</div>;
      $[60] = t19;
      $[61] = t20;
    } else {
      t20 = $[61];
    }
    let t21;
    if ($[62] !== t) {
      t21 = t("playground.audioSignInDescription");
      $[62] = t;
      $[63] = t21;
    } else {
      t21 = $[63];
    }
    let t22;
    if ($[64] !== t21) {
      t22 = <p className="text-muted-foreground mb-3">{t21}</p>;
      $[64] = t21;
      $[65] = t22;
    } else {
      t22 = $[65];
    }
    let t23;
    if ($[66] !== t) {
      t23 = t("auth.signIn");
      $[66] = t;
      $[67] = t23;
    } else {
      t23 = $[67];
    }
    let t24;
    if ($[68] !== t23) {
      t24 = <Button size="sm" asChild={true}><Link href="/login">{t23}</Link></Button>;
      $[68] = t23;
      $[69] = t24;
    } else {
      t24 = $[69];
    }
    let t25;
    if ($[70] !== t) {
      t25 = t("auth.createAccount");
      $[70] = t;
      $[71] = t25;
    } else {
      t25 = $[71];
    }
    let t26;
    if ($[72] !== t25) {
      t26 = <Button size="sm" variant="outline" asChild={true}><Link href="/signup">{t25}</Link></Button>;
      $[72] = t25;
      $[73] = t26;
    } else {
      t26 = $[73];
    }
    let t27;
    if ($[74] !== t24 || $[75] !== t26) {
      t27 = <div className="flex items-center justify-end gap-2">{t24}{t26}</div>;
      $[74] = t24;
      $[75] = t26;
      $[76] = t27;
    } else {
      t27 = $[76];
    }
    let t28;
    if ($[77] !== t20 || $[78] !== t22 || $[79] !== t27) {
      t28 = <div className="w-full rounded-md border p-4 text-sm">{t20}{t22}{t27}</div>;
      $[77] = t20;
      $[78] = t22;
      $[79] = t27;
      $[80] = t28;
    } else {
      t28 = $[80];
    }
    let t29;
    if ($[81] !== t18 || $[82] !== t28) {
      t29 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t18}{t28}</div></SidebarHeader>;
      $[81] = t18;
      $[82] = t28;
      $[83] = t29;
    } else {
      t29 = $[83];
    }
    let t30;
    if ($[84] !== className || $[85] !== t29) {
      t30 = <Sidebar className={className}>{t29}</Sidebar>;
      $[84] = className;
      $[85] = t29;
      $[86] = t30;
    } else {
      t30 = $[86];
    }
    return t30;
  }
  const t15 = (className ?? "") + " max-md:hidden";
  let t16;
  if ($[87] !== withOrg) {
    t16 = withOrg("/");
    $[87] = withOrg;
    $[88] = t16;
  } else {
    t16 = $[88];
  }
  let t17;
  let t18;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="flex aspect-square size-8 items-center justify-center"><Logo className="size-6" /></div>;
    t18 = <span className="text-lg font-bold tracking-tight">doteb</span>;
    $[89] = t17;
    $[90] = t18;
  } else {
    t17 = $[89];
    t18 = $[90];
  }
  let t19;
  if ($[91] !== t) {
    t19 = t("playground.chat");
    $[91] = t;
    $[92] = t19;
  } else {
    t19 = $[92];
  }
  let t20;
  if ($[93] !== t19) {
    t20 = <Badge variant="secondary" className="group-data-[collapsible=icon]:hidden">{t19}</Badge>;
    $[93] = t19;
    $[94] = t20;
  } else {
    t20 = $[94];
  }
  let t21;
  if ($[95] !== t16 || $[96] !== t20) {
    t21 = <SidebarMenuItem><SidebarMenuButton size="lg" asChild={true} tooltip="doteb"><Link href={t16} prefetch={true}>{t17}{t18}{t20}</Link></SidebarMenuButton></SidebarMenuItem>;
    $[95] = t16;
    $[96] = t20;
    $[97] = t21;
  } else {
    t21 = $[97];
  }
  let t22;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <SidebarChatSearch disabled={true} />;
    $[98] = t22;
  } else {
    t22 = $[98];
  }
  let t23;
  if ($[99] !== t) {
    t23 = t("playground.newGeneration");
    $[99] = t;
    $[100] = t23;
  } else {
    t23 = $[100];
  }
  let t24;
  if ($[101] !== onNewChat || $[102] !== t23) {
    t24 = <SidebarNewAction label={t23} onAction={onNewChat} />;
    $[101] = onNewChat;
    $[102] = t23;
    $[103] = t24;
  } else {
    t24 = $[103];
  }
  let t25;
  if ($[104] !== t) {
    t25 = t("playground.chat");
    $[104] = t;
    $[105] = t25;
  } else {
    t25 = $[105];
  }
  const t26 = pathname === "/";
  let t27;
  if ($[106] !== withOrg) {
    t27 = withOrg("/");
    $[106] = withOrg;
    $[107] = t27;
  } else {
    t27 = $[107];
  }
  let t28;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <MessageSquare className="h-4 w-4" />;
    $[108] = t28;
  } else {
    t28 = $[108];
  }
  let t29;
  if ($[109] !== t) {
    t29 = t("playground.chat");
    $[109] = t;
    $[110] = t29;
  } else {
    t29 = $[110];
  }
  let t30;
  if ($[111] !== t29) {
    t30 = <span>{t29}</span>;
    $[111] = t29;
    $[112] = t30;
  } else {
    t30 = $[112];
  }
  let t31;
  if ($[113] !== t27 || $[114] !== t30) {
    t31 = <Link href={t27} prefetch={true}>{t28}{t30}</Link>;
    $[113] = t27;
    $[114] = t30;
    $[115] = t31;
  } else {
    t31 = $[115];
  }
  let t32;
  if ($[116] !== t25 || $[117] !== t26 || $[118] !== t31) {
    t32 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t25} isActive={t26}>{t31}</SidebarMenuButton></SidebarMenuItem>;
    $[116] = t25;
    $[117] = t26;
    $[118] = t31;
    $[119] = t32;
  } else {
    t32 = $[119];
  }
  let t33;
  if ($[120] !== t) {
    t33 = t("playground.groupChat");
    $[120] = t;
    $[121] = t33;
  } else {
    t33 = $[121];
  }
  const t34 = pathname === "/group";
  let t35;
  if ($[122] !== withOrg) {
    t35 = withOrg("/group");
    $[122] = withOrg;
    $[123] = t35;
  } else {
    t35 = $[123];
  }
  let t36;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <Users className="h-4 w-4" />;
    $[124] = t36;
  } else {
    t36 = $[124];
  }
  let t37;
  if ($[125] !== t) {
    t37 = t("playground.groupChat");
    $[125] = t;
    $[126] = t37;
  } else {
    t37 = $[126];
  }
  let t38;
  if ($[127] !== t37) {
    t38 = <span>{t37}</span>;
    $[127] = t37;
    $[128] = t38;
  } else {
    t38 = $[128];
  }
  let t39;
  if ($[129] !== t35 || $[130] !== t38) {
    t39 = <Link href={t35} prefetch={true}>{t36}{t38}</Link>;
    $[129] = t35;
    $[130] = t38;
    $[131] = t39;
  } else {
    t39 = $[131];
  }
  let t40;
  if ($[132] !== t33 || $[133] !== t34 || $[134] !== t39) {
    t40 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t33} isActive={t34}>{t39}</SidebarMenuButton></SidebarMenuItem>;
    $[132] = t33;
    $[133] = t34;
    $[134] = t39;
    $[135] = t40;
  } else {
    t40 = $[135];
  }
  let t41;
  if ($[136] !== t) {
    t41 = t("playground.imageStudio");
    $[136] = t;
    $[137] = t41;
  } else {
    t41 = $[137];
  }
  const t42 = pathname === "/image";
  let t43;
  if ($[138] !== withOrg) {
    t43 = withOrg("/image");
    $[138] = withOrg;
    $[139] = t43;
  } else {
    t43 = $[139];
  }
  let t44;
  if ($[140] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <ImageIcon className="h-4 w-4" />;
    $[140] = t44;
  } else {
    t44 = $[140];
  }
  let t45;
  if ($[141] !== t) {
    t45 = t("playground.imageStudio");
    $[141] = t;
    $[142] = t45;
  } else {
    t45 = $[142];
  }
  let t46;
  if ($[143] !== t45) {
    t46 = <span>{t45}</span>;
    $[143] = t45;
    $[144] = t46;
  } else {
    t46 = $[144];
  }
  let t47;
  if ($[145] !== t43 || $[146] !== t46) {
    t47 = <Link href={t43} prefetch={true}>{t44}{t46}</Link>;
    $[145] = t43;
    $[146] = t46;
    $[147] = t47;
  } else {
    t47 = $[147];
  }
  let t48;
  if ($[148] !== t41 || $[149] !== t42 || $[150] !== t47) {
    t48 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t41} isActive={t42}>{t47}</SidebarMenuButton></SidebarMenuItem>;
    $[148] = t41;
    $[149] = t42;
    $[150] = t47;
    $[151] = t48;
  } else {
    t48 = $[151];
  }
  let t49;
  if ($[152] !== t) {
    t49 = t("playground.videoStudio");
    $[152] = t;
    $[153] = t49;
  } else {
    t49 = $[153];
  }
  const t50 = pathname === "/video";
  let t51;
  if ($[154] !== withOrg) {
    t51 = withOrg("/video");
    $[154] = withOrg;
    $[155] = t51;
  } else {
    t51 = $[155];
  }
  let t52;
  if ($[156] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <Film className="h-4 w-4" />;
    $[156] = t52;
  } else {
    t52 = $[156];
  }
  let t53;
  if ($[157] !== t) {
    t53 = t("playground.videoStudio");
    $[157] = t;
    $[158] = t53;
  } else {
    t53 = $[158];
  }
  let t54;
  if ($[159] !== t53) {
    t54 = <span>{t53}</span>;
    $[159] = t53;
    $[160] = t54;
  } else {
    t54 = $[160];
  }
  let t55;
  if ($[161] !== t51 || $[162] !== t54) {
    t55 = <Link href={t51} prefetch={true}>{t52}{t54}</Link>;
    $[161] = t51;
    $[162] = t54;
    $[163] = t55;
  } else {
    t55 = $[163];
  }
  let t56;
  if ($[164] !== t49 || $[165] !== t50 || $[166] !== t55) {
    t56 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t49} isActive={t50}>{t55}</SidebarMenuButton></SidebarMenuItem>;
    $[164] = t49;
    $[165] = t50;
    $[166] = t55;
    $[167] = t56;
  } else {
    t56 = $[167];
  }
  let t57;
  if ($[168] !== t) {
    t57 = t("playground.audioStudio");
    $[168] = t;
    $[169] = t57;
  } else {
    t57 = $[169];
  }
  const t58 = pathname === "/audio";
  let t59;
  if ($[170] !== withOrg) {
    t59 = withOrg("/audio");
    $[170] = withOrg;
    $[171] = t59;
  } else {
    t59 = $[171];
  }
  let t60;
  if ($[172] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <AudioLines className="h-4 w-4" />;
    $[172] = t60;
  } else {
    t60 = $[172];
  }
  let t61;
  if ($[173] !== t) {
    t61 = t("playground.audioStudio");
    $[173] = t;
    $[174] = t61;
  } else {
    t61 = $[174];
  }
  let t62;
  if ($[175] !== t61) {
    t62 = <span>{t61}</span>;
    $[175] = t61;
    $[176] = t62;
  } else {
    t62 = $[176];
  }
  let t63;
  if ($[177] !== t59 || $[178] !== t62) {
    t63 = <Link href={t59} prefetch={true}>{t60}{t62}</Link>;
    $[177] = t59;
    $[178] = t62;
    $[179] = t63;
  } else {
    t63 = $[179];
  }
  let t64;
  if ($[180] !== t57 || $[181] !== t58 || $[182] !== t63) {
    t64 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t57} isActive={t58}>{t63}</SidebarMenuButton></SidebarMenuItem>;
    $[180] = t57;
    $[181] = t58;
    $[182] = t63;
    $[183] = t64;
  } else {
    t64 = $[183];
  }
  let t65;
  if ($[184] !== t) {
    t65 = t("playground.canvas");
    $[184] = t;
    $[185] = t65;
  } else {
    t65 = $[185];
  }
  const t66 = pathname === "/canvas";
  let t67;
  if ($[186] !== withOrg) {
    t67 = withOrg("/canvas");
    $[186] = withOrg;
    $[187] = t67;
  } else {
    t67 = $[187];
  }
  let t68;
  if ($[188] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = <PenTool className="h-4 w-4" />;
    $[188] = t68;
  } else {
    t68 = $[188];
  }
  let t69;
  if ($[189] !== t) {
    t69 = t("playground.canvas");
    $[189] = t;
    $[190] = t69;
  } else {
    t69 = $[190];
  }
  let t70;
  if ($[191] !== t69) {
    t70 = <span>{t69}</span>;
    $[191] = t69;
    $[192] = t70;
  } else {
    t70 = $[192];
  }
  let t71;
  if ($[193] !== t67 || $[194] !== t70) {
    t71 = <Link href={t67} prefetch={true}>{t68}{t70}</Link>;
    $[193] = t67;
    $[194] = t70;
    $[195] = t71;
  } else {
    t71 = $[195];
  }
  let t72;
  if ($[196] !== t65 || $[197] !== t66 || $[198] !== t71) {
    t72 = <SidebarMenuItem><SidebarMenuButton asChild={true} tooltip={t65} isActive={t66}>{t71}</SidebarMenuButton></SidebarMenuItem>;
    $[196] = t65;
    $[197] = t66;
    $[198] = t71;
    $[199] = t72;
  } else {
    t72 = $[199];
  }
  let t73;
  if ($[200] !== t21 || $[201] !== t24 || $[202] !== t32 || $[203] !== t40 || $[204] !== t48 || $[205] !== t56 || $[206] !== t64 || $[207] !== t72) {
    t73 = <SidebarHeader><SidebarMenu>{t21}{t22}{t24}{t32}{t40}{t48}{t56}{t64}{t72}</SidebarMenu></SidebarHeader>;
    $[200] = t21;
    $[201] = t24;
    $[202] = t32;
    $[203] = t40;
    $[204] = t48;
    $[205] = t56;
    $[206] = t64;
    $[207] = t72;
    $[208] = t73;
  } else {
    t73 = $[208];
  }
  let t74;
  if ($[209] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <div className="mx-2 mb-2 border-t border-sidebar-border" />;
    $[209] = t74;
  } else {
    t74 = $[209];
  }
  let t75;
  if ($[210] !== onSelectOrganization || $[211] !== switcherOrganizations || $[212] !== switcherSelectedOrganization) {
    t75 = switcherOrganizations.length > 0 ? <SidebarMenu className="px-2 pb-2 group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={switcherOrganizations} selectedOrganization={switcherSelectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu> : null;
    $[210] = onSelectOrganization;
    $[211] = switcherOrganizations;
    $[212] = switcherSelectedOrganization;
    $[213] = t75;
  } else {
    t75 = $[213];
  }
  let t76;
  if ($[214] !== t75) {
    t76 = <div>{t74}{t75}</div>;
    $[214] = t75;
    $[215] = t76;
  } else {
    t76 = $[215];
  }
  let t77;
  if ($[216] !== galleryItems || $[217] !== historyRows || $[218] !== isHistoryLoading || $[219] !== rowProps || $[220] !== t) {
    t77 = isHistoryLoading && galleryItems.length === 0 ? <HistorySkeleton withThumbnail={true} /> : galleryItems.length === 0 ? <div className="flex flex-col items-center justify-center py-8 text-center"><AudioLines className="h-12 w-12 text-muted-foreground/50 mb-4" /><p className="text-sm text-muted-foreground mb-2">{t("playground.noGenerationHistory")}</p><p className="text-xs text-muted-foreground">{t("playground.audioHistoryDescription")}</p></div> : <List className="min-h-0 w-full flex-1" style={{
      width: "100%"
    }} rowComponent={AudioHistoryRowComponent} rowCount={historyRows.length} rowHeight={getAudioHistoryRowHeight} rowProps={rowProps} overscanCount={8} />;
    $[216] = galleryItems;
    $[217] = historyRows;
    $[218] = isHistoryLoading;
    $[219] = rowProps;
    $[220] = t;
    $[221] = t77;
  } else {
    t77 = $[221];
  }
  let t78;
  if ($[222] !== isHistoryHidden || $[223] !== t77) {
    t78 = <div ref={listContainerRef} aria-hidden={isHistoryHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t77}</div>;
    $[222] = isHistoryHidden;
    $[223] = t77;
    $[224] = t78;
  } else {
    t78 = $[224];
  }
  let t79;
  if ($[225] !== t76 || $[226] !== t78) {
    t79 = <SidebarContent className="overflow-hidden pb-2">{t76}{t78}</SidebarContent>;
    $[225] = t76;
    $[226] = t78;
    $[227] = t79;
  } else {
    t79 = $[227];
  }
  const t80 = switcherSelectedOrganization ?? organization;
  const t81 = !switcherSelectedOrganization;
  let t82;
  if ($[228] !== isOrgLoading || $[229] !== t80 || $[230] !== t81) {
    t82 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={t80} isLoading={isOrgLoading} isChatPlanOrg={t81} /></div>;
    $[228] = isOrgLoading;
    $[229] = t80;
    $[230] = t81;
    $[231] = t82;
  } else {
    t82 = $[231];
  }
  let t83;
  if ($[232] !== t || $[233] !== user?.name) {
    t83 = user?.name ?? t("playground.user");
    $[232] = t;
    $[233] = user?.name;
    $[234] = t83;
  } else {
    t83 = $[234];
  }
  let t84;
  if ($[235] !== user?.name) {
    t84 = user?.name?.split(" ").map(_temp6).join("").toUpperCase().slice(0, 2) ?? "U";
    $[235] = user?.name;
    $[236] = t84;
  } else {
    t84 = $[236];
  }
  let t85;
  if ($[237] !== t84) {
    t85 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t84}</span></div>;
    $[237] = t84;
    $[238] = t85;
  } else {
    t85 = $[238];
  }
  const t86 = user?.name;
  let t87;
  if ($[239] !== t86) {
    t87 = <span className="truncate font-semibold">{t86}</span>;
    $[239] = t86;
    $[240] = t87;
  } else {
    t87 = $[240];
  }
  const t88 = user?.email;
  let t89;
  if ($[241] !== t88) {
    t89 = <span className="truncate text-xs text-muted-foreground">{t88}</span>;
    $[241] = t88;
    $[242] = t89;
  } else {
    t89 = $[242];
  }
  let t90;
  if ($[243] !== t87 || $[244] !== t89) {
    t90 = <div className="grid flex-1 text-left text-sm leading-tight">{t87}{t89}</div>;
    $[243] = t87;
    $[244] = t89;
    $[245] = t90;
  } else {
    t90 = $[245];
  }
  let t91;
  if ($[246] === Symbol.for("react.memo_cache_sentinel")) {
    t91 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[246] = t91;
  } else {
    t91 = $[246];
  }
  let t92;
  if ($[247] !== t83 || $[248] !== t85 || $[249] !== t90) {
    t92 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t83} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t85}{t90}{t91}</SidebarMenuButton></DropdownMenuTrigger>;
    $[247] = t83;
    $[248] = t85;
    $[249] = t90;
    $[250] = t92;
  } else {
    t92 = $[250];
  }
  let t93;
  if ($[251] === Symbol.for("react.memo_cache_sentinel")) {
    t93 = process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : getMarketingUrl("/dashboard");
    $[251] = t93;
  } else {
    t93 = $[251];
  }
  let t94;
  if ($[252] === Symbol.for("react.memo_cache_sentinel")) {
    t94 = <ExternalLink className="mr-2 h-4 w-4" />;
    $[252] = t94;
  } else {
    t94 = $[252];
  }
  let t95;
  if ($[253] !== t) {
    t95 = t("playground.dashboard");
    $[253] = t;
    $[254] = t95;
  } else {
    t95 = $[254];
  }
  let t96;
  if ($[255] !== t95) {
    t96 = <DropdownMenuItem asChild={true}><a href={t93} target="_blank" rel="noopener noreferrer">{t94}{t95}</a></DropdownMenuItem>;
    $[255] = t95;
    $[256] = t96;
  } else {
    t96 = $[256];
  }
  let t97;
  if ($[257] === Symbol.for("react.memo_cache_sentinel")) {
    t97 = <DropdownMenuSeparator />;
    $[257] = t97;
  } else {
    t97 = $[257];
  }
  let t98;
  if ($[258] !== toggleTheme) {
    t98 = event => {
      event.preventDefault();
      toggleTheme();
    };
    $[258] = toggleTheme;
    $[259] = t98;
  } else {
    t98 = $[259];
  }
  let t99;
  if ($[260] !== t) {
    t99 = t("playground.theme");
    $[260] = t;
    $[261] = t99;
  } else {
    t99 = $[261];
  }
  let t100;
  if ($[262] !== t99) {
    t100 = <span>{t99}</span>;
    $[262] = t99;
    $[263] = t100;
  } else {
    t100 = $[263];
  }
  let t101;
  if ($[264] === Symbol.for("react.memo_cache_sentinel")) {
    t101 = <div onClick={_temp7} onKeyDown={_temp8}><ThemeToggle className="shrink-0" size="compact" /></div>;
    $[264] = t101;
  } else {
    t101 = $[264];
  }
  let t102;
  if ($[265] !== t100 || $[266] !== t98) {
    t102 = <DropdownMenuItem className="justify-between gap-3" onSelect={t98}>{t100}{t101}</DropdownMenuItem>;
    $[265] = t100;
    $[266] = t98;
    $[267] = t102;
  } else {
    t102 = $[267];
  }
  let t103;
  if ($[268] === Symbol.for("react.memo_cache_sentinel")) {
    t103 = <DropdownMenuSeparator />;
    $[268] = t103;
  } else {
    t103 = $[268];
  }
  let t104;
  if ($[269] === Symbol.for("react.memo_cache_sentinel")) {
    t104 = <LogOut className="mr-2 h-4 w-4" />;
    $[269] = t104;
  } else {
    t104 = $[269];
  }
  let t105;
  if ($[270] !== t) {
    t105 = t("playground.logOut");
    $[270] = t;
    $[271] = t105;
  } else {
    t105 = $[271];
  }
  let t106;
  if ($[272] !== logout || $[273] !== t105) {
    t106 = <DropdownMenuItem onClick={logout}>{t104}{t105}</DropdownMenuItem>;
    $[272] = logout;
    $[273] = t105;
    $[274] = t106;
  } else {
    t106 = $[274];
  }
  let t107;
  if ($[275] !== t102 || $[276] !== t106 || $[277] !== t96) {
    t107 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t96}{t97}{t102}{t103}{t106}</DropdownMenuContent>;
    $[275] = t102;
    $[276] = t106;
    $[277] = t96;
    $[278] = t107;
  } else {
    t107 = $[278];
  }
  let t108;
  if ($[279] !== t107 || $[280] !== t92) {
    t108 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t92}{t107}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[279] = t107;
    $[280] = t92;
    $[281] = t108;
  } else {
    t108 = $[281];
  }
  let t109;
  if ($[282] !== t108 || $[283] !== t82) {
    t109 = <SidebarFooter>{t82}{t108}</SidebarFooter>;
    $[282] = t108;
    $[283] = t82;
    $[284] = t109;
  } else {
    t109 = $[284];
  }
  let t110;
  if ($[285] !== t109 || $[286] !== t15 || $[287] !== t73 || $[288] !== t79) {
    t110 = <Sidebar collapsible="icon" className={t15}>{t73}{t79}{t109}</Sidebar>;
    $[285] = t109;
    $[286] = t15;
    $[287] = t73;
    $[288] = t79;
    $[289] = t110;
  } else {
    t110 = $[289];
  }
  return t110;
}
function _temp8(event_1) {
  return event_1.stopPropagation();
}
function _temp7(event_0) {
  return event_0.stopPropagation();
}
function _temp6(n) {
  return n[0];
}
function _temp5(group) {
  return group.items.length > 0;
}
function _temp4(a, b) {
  return b.timestamp - a.timestamp;
}
function _temp3(org) {
  return !org.isPersonal && !org.isChat;
}